import axios from 'axios';
import { normalizeRestLink } from '../components/common/utils';
import { push } from 'react-router-redux';
import delay from 'lodash-es/delay';
import { appConfig } from '../config';
import { compareAsc, parseISO } from 'date-fns';
import {
  fetchBillingCycleTypeList,
  fetchCustomerTypeList,
  fetchFeeTypesList,
  fetchServiceList,
  fetchSmsStatusList,
  fetchStatusList,
  fetchUnauthorizedStatusList,
  fetchUserTagsList,
  getLogo,
  getMenu,
} from './app';
import { setAgentStatus } from './chat';
import { getAppearance } from './appearance';
import { AppThunkAction } from './index';
import { AnyAction } from 'redux';
import authenticationApi from 'src/api/authentication/authenticationApi';
import { getPendingForApprovalViberTemplatesForAdmin } from './templates/viber';
import viberApi from '../api/viber/viberApi';
import { ViberAccountConfig } from '../types/viber/viberAccount';
import metaApi from '../api/meta/metaApi';
import { WhatsappAccountConfig } from '../types/meta/metaAccount';
import { showSnackBarMsg } from './snackbar';
import { FormattedMessage } from 'react-intl';
import { getDefaultMetaConfig } from './meta';
import { showToast } from 'src/reducers/slices/toast';
import { AppVersion } from 'src/appVersion';
import { analyticsService, ANALYTICS_EVENTS } from 'src/service/analytics/analytics.service';
import { SESSION_VALIDATION_URL } from 'src/api/common/sessionValidation';

const UNAUTHORIZED = 401;
let sessionValidationRequest: Promise<void> | null = null;

export function doLogout(redirect: boolean = true): AppThunkAction {
  return (dispatch, getState) => {
    const agent = getState().login.userData.chatAgent;
    if (agent) dispatch(setAgentStatus('offline'));

    const authMethod = getState().login.userData.authMethod;
    const userName = getState().login.userData.userName;
    analyticsService.track(ANALYTICS_EVENTS.LogOutCompleted, {
      sign_in_method: authMethod ?? 'unknown',
      user_id: userName ?? null,
    });

    localStorage.removeItem('token');
    localStorage.removeItem('chatToken');
    localStorage.removeItem('redux');
    localStorage.removeItem('logo');
    localStorage.removeItem('columns-sms-cdrs');
    localStorage.removeItem('columns-viber-cdrs');
    localStorage.removeItem('columns-whatsapp-cdrs');
    localStorage.removeItem('columns-invoices');
    localStorage.removeItem('columns-balanceOverview');
    localStorage.removeItem('columns-accessLog');
    localStorage.removeItem('columns-finance');
    localStorage.removeItem('columns-campaignContacts');
    localStorage.removeItem('columns-campaignLog');
    localStorage.removeItem('columns-contacts');
    localStorage.removeItem('columns-uploadingContacts');
    dispatch({ type: 'IS_NOT_AUTHENTICATED', payload: 'unauthenticated' });

    if (redirect) {
      if (authMethod === 'legacy') {
        dispatch(push('/login'));
      } else if (authMethod === 'oauth2') {
        authenticationApi.oauth2.logoutRedirect();
      }
    }
  };
}

export function sessionExpired(): AppThunkAction {
  return (dispatch, getState) => {
    // Short-circuit if the client is already logged out so concurrent 401
    // responses (e.g. several RTK Query subscriptions on the same page) do
    // not trigger duplicate snackbars or redirect dispatches.
    if (!getState().login.authenticated) {
      return;
    }
    dispatch(showSnackBarMsg(<FormattedMessage id="login.session.expired.info" />, 'info'));
    dispatch(doLogout());
  };
}

/**
 * Confirms that a 401 from a feature endpoint was caused by an expired
 * authentication session. Feature APIs also use 401 for domain-level access
 * failures, so only a 401 from the canonical user endpoint may log the user
 * out. Concurrent failures share the same validation request.
 */
export function validateSession(): AppThunkAction<Promise<void>> {
  return (dispatch, getState) => {
    if (!getState().login.authenticated) {
      return Promise.resolve();
    }

    if (!sessionValidationRequest) {
      const config = {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      };

      sessionValidationRequest = axios
        .get(SESSION_VALIDATION_URL, config)
        .then(() => undefined)
        .catch((error) => {
          if (error?.response?.status === UNAUTHORIZED) {
            dispatch(sessionExpired());
          }
        })
        .finally(() => {
          sessionValidationRequest = null;
        });
    }

    return sessionValidationRequest;
  };
}

export function isAuthenticated(): AppThunkAction<Promise<void>> {
  return (dispatch) =>
    new Promise((resolve) => {
      const config = {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      };
      axios
        .get(SESSION_VALIDATION_URL, config)
        .then((response: $TSFixMe) => {
          if (!response.data.active) {
            dispatch(doLogout());
          }
          dispatch({ type: 'IS_AUTHENTICATED', payload: response.data });
          if (response.data.authorizations['SUPER_ADMIN']) {
            dispatch(fetchFeeTypesList());
            dispatch(getPendingForApprovalViberTemplatesForAdmin());
          }
          resolve(response);
        })
        .catch((err) => {
          if (err?.response?.status === UNAUTHORIZED) {
            dispatch(doLogout());
          }
        });
    });
}

export function openPage(): AppThunkAction {
  return (dispatch) => {
    dispatch({ type: 'REFRESH_LOGIN_PAGE', payload: '' });
  };
}

export type AuthTokenResponse = {
  readonly token: string;
  readonly chatToken: string;
  readonly authMethod: 'oauth2' | 'legacy';
};

export function doLogin(username: string, password: string, code?: string, redirectUrl?: string): AppThunkAction {
  if (username && password) {
    return (dispatch, getState) => {
      dispatch({ type: 'LOGIN_START', isLoading: true });

      axios
        .post(`${appConfig.URL}api/authentication`, { email: username, password: password, ...(code && { code }) })
        .then((response) => {
          if (response.data['2fa'] === 'SMS') {
            dispatch(setSmsVerificationDialogShow(true));
            dispatch({ type: 'LOGIN_STOP', isLoading: false });
          } else {
            // Ad hoc workaround to support new ui with legacy auth method
            const appVersion = AppVersion.parse(appConfig.REACT_APP_VERSION);
            if (appVersion.isMajor(3)) {
              const { token, chatToken } = response.data;
              const authMethod: 'legacy' | 'oauth2' = response.data.authMethod ?? 'legacy';
              localStorage.removeItem('token');
              localStorage.setItem('token', token);
              localStorage.setItem('chatToken', chatToken);

              dispatch({
                type: 'ADD_SNACKBAR',
                variant: 'success',
                msg: 'Login successfull',
              });

              dispatch(isAuthenticated()).then(() => {
                dispatch(setSmsVerificationDialogShow(false));
                dispatch(fetchStatusList());
                dispatch(fetchServiceList());
                dispatch(fetchSmsStatusList());
                dispatch(fetchUnauthorizedStatusList());
                dispatch(fetchCustomerTypeList());
                dispatch(fetchBillingCycleTypeList());
                dispatch(fetchUserTagsList());
                dispatch(push(redirectUrl ? redirectUrl : '/dashboard'));
                dispatch({ type: 'PROCESS_SNACKBAR_QUEUE' });
                dispatch({
                  type: 'LOGIN_SUCCESS',
                  payload: {
                    ...response.data,
                    authMethod,
                  },
                });
                dispatch(getLogo());
                dispatch(getAppearance());
                dispatch(getMenu());
                dispatch(showWelcomeScreen());
                dispatch(getAccountInfo());
                dispatch(getUserViberConfig());
                dispatch(getUserWhatsappConfig());
                dispatch(getDefaultMetaConfig());
                analyticsService.track(ANALYTICS_EVENTS.SignInCompleted, {
                  sign_in_method: authMethod ?? 'legacy',
                });
                return;
              });

              return;
            }

            const { token, chatToken } = response.data;
            dispatch(doPostLoginActions({ token, chatToken, authMethod: 'legacy' }, redirectUrl));
          }
        })
        .catch((err) => {
          
          if (err.response === undefined) {
            dispatch({
              type: 'LOGIN_FAILED',
              payload: { message: 'loginFailed' },
            });
          } else {
            if (code) {
              dispatch({
                type: 'SMS_VERIFICATION_FAILED',
                payload: { message: 'smsVerificationFailed' },
              });
              dispatch({
                type: 'LOGIN_STOP',
                isLoading: false,
              });
            } else {
              dispatch({
                type: 'LOGIN_FAILED',
                payload: { message: 'authenticationFailed' },
              });
            }
            delay(() => dispatch({ type: 'CLEAN_FAILED' }), 5000);
          }
        });
    };
  }
  return (dispatch) => {
    dispatch({ type: 'LOGIN_EMPTY', payload: { message: 'EmptyPWD' } });
    delay(() => dispatch({ type: 'CLEAN_FAILED' }), 5000);
  };
}

export const doPostLoginActions = (authTokenResponse: AuthTokenResponse, redirectUrl?: string): AppThunkAction => {
  return (dispatch) => {
    const { token, chatToken, authMethod } = authTokenResponse;
    localStorage.removeItem('token');
    localStorage.setItem('token', token);
    localStorage.setItem('chatToken', chatToken);

    // dispatch({
    //   type: 'ADD_SNACKBAR',
    //   variant: 'success',
    //   msg: 'Login successfull',
    // });

    dispatch(isAuthenticated()).then(() => {
      dispatch(setSmsVerificationDialogShow(false));
      dispatch(fetchStatusList());
      dispatch(fetchServiceList());
      dispatch(fetchSmsStatusList());
      dispatch(fetchUnauthorizedStatusList());
      dispatch(fetchCustomerTypeList());
      dispatch(fetchBillingCycleTypeList());
      dispatch(fetchUserTagsList());
      // dispatch(push(redirectUrl ? redirectUrl : '/dashboard'));
      dispatch({ type: 'PROCESS_SNACKBAR_QUEUE' });
      dispatch({ type: 'LOGIN_SUCCESS', payload: authTokenResponse });
      dispatch(getLogo());
      dispatch(getAppearance());
      dispatch(getMenu());
      dispatch(showWelcomeScreen());
      dispatch(getAccountInfo());
      dispatch(getUserViberConfig());
      dispatch(getUserWhatsappConfig());
      dispatch(getDefaultMetaConfig());
      analyticsService.track(ANALYTICS_EVENTS.SignInCompleted, {
        sign_in_method: authMethod ?? 'unknown',
      });
    });
  };
};

export const showWelcomeScreen = (): AppThunkAction => {
  return (dispatch, getState) => {
    const hideTestAccountWelcomeScreenNextTime = localStorage.getItem('hideTestAccountWelcomeScreenNextTime');
    const { userData } = getState().login;
    const url = userData._links && userData._links.account_info && userData._links.account_info.href;
    const normalizedUrl = url ? normalizeRestLink(url) : url;
    const config = {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    };
    if (normalizedUrl) {
      axios
        .get(normalizedUrl, config)
        .then((res) => {
          if (res.data.customerType === 'DEMO' && !hideTestAccountWelcomeScreenNextTime) {
            dispatch({
              type: 'SET_SHOW_TEST_ACCOUNT_WELCOME_SCREEN',
              payload: true,
            });
          }
        })
        .catch((err) => {
          
        });
    }
  };
};

export const getAccountInfo = (): AppThunkAction => {
  return (dispatch, getState) => {
    const config = {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    };
    const { userData } = getState().login;
    const url = userData._links && userData._links.account_info && userData._links.account_info.href;
    const normalizedUrl = url ? normalizeRestLink(url) : url;
    if (normalizedUrl) {
      axios
        .get(normalizedUrl, config)
        .then((res) => {
          if (res.data.customerType === 'DEMO') {
            dispatch({ type: 'SET_ACCOUNT_INFO', payload: res.data });
            if (compareAsc(parseISO(res.data.validTo), new Date()) === -1) {
              delay(() => {
                dispatch({
                  type: 'SET_SHOW_TEST_ACCOUNT_EXPIRED_SCREEN',
                  payload: true,
                });
              }, 200);
            }
          }
        })
        .catch((err) => {
          
        });
    }
  };
};

export function setSmsVerificationDialogShow(flag: boolean): AnyAction {
  return { type: 'SET_SMS_VERIFICATION_CODE_DIALOG_SHOW', payload: flag };
}

export function getUserViberConfig(): AppThunkAction {
  return (dispatch, getState) => {
    const tenantId = getState().login.userData.customerUuid!;
    viberApi.config
      .getUserConfig(tenantId)
      .then((userConfig: ViberAccountConfig) => {
        userConfig &&
          dispatch({
            type: 'SET_USER_VIBER_CONFIG',
            payload: userConfig.featureFlags,
          });
      })
      .catch((error) => {
        
      });
  };
}

export function getUserWhatsappConfig(): AppThunkAction {
  return (dispatch, getState) => {
    const tenantId = getState().login.userData.customerUuid!;
    metaApi.whatsappConfig
      .getUserConfig(tenantId)
      .then((userConfig: WhatsappAccountConfig) => {
        userConfig &&
          dispatch({
            type: 'SET_USER_WHATSAPP_CONFIG',
            payload: userConfig.featureFlags,
          });
      })
      .catch((error) => {
        
      });
  };
}

export function requestNewCode({ username, password }: { username: string; password: string }): AppThunkAction {
  return (dispatch) => {
    authenticationApi.legacy
      .requestNew2faCode(username, password)
      .then((authorization) => {
        if (authorization['2fa'] === 'SMS') {
          dispatch(setSmsVerificationDialogShow(true));
          dispatch({ type: 'LOGIN_STOP', isLoading: false });
          dispatch(
            showToast({
              title: <FormattedMessage id="components.toast.success.title" />,
              message: <FormattedMessage id="login.verification.new.code.sent" />,
              variant: 'success',
            })
          );
        }
      })
      .catch(() => {
        dispatch(
          showToast({
            title: <FormattedMessage id="components.toast.error.title" />,
            message: <FormattedMessage id="common.label.error" />,
            variant: 'error',
          })
        );
      });
  };
}
